import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_aurobindo-kids/artifacts/z058rndb_Aurobindo_Logo_WhiteBackgnd.png';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Our Promise', path: '/our-promise' },
  { label: 'Montessori', path: '/montessori' },
  { label: 'Programs', path: '/programs' },
  { label: 'Admissions', path: '/admissions' },
  { label: 'Gallery', path: '/gallery' },
  { label: 'Testimonials', path: '/testimonials' },
  { label: 'Activities', path: '/extra-curricular' },
  { label: 'Alumni', path: '/alumni' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setIsOpen(false); }, [location]);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-[0_2px_20px_rgba(30,58,138,0.12)]' : 'bg-white/98 backdrop-blur-sm'}`} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 lg:h-18">

          {/* Logo + Name */}
          <Link to="/" className="flex items-center gap-2.5 flex-shrink-0" data-testid="nav-logo">
            <img src={LOGO_URL} alt="Sri Aurobindo Montessori Logo" className="h-10 w-10 object-contain rounded-xl" />
            <div className="leading-tight">
              <p className="text-[#1E3A8A] font-black text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>Sri Aurobindo</p>
              <p className="text-[#1E3A8A] text-xs font-semibold opacity-80">Montessori House of Children</p>
            </div>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center gap-0.5" role="navigation">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`px-2.5 py-2 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap ${
                  location.pathname === link.path
                    ? 'bg-blue-100 text-[#1E3A8A]'
                    : 'text-slate-600 hover:text-[#1E3A8A] hover:bg-blue-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
            <Link
              to="/admissions"
              data-testid="nav-enquire-btn"
              className="bg-[#1E3A8A] text-white rounded-full px-5 py-2 text-xs font-bold hover:bg-[#1E40AF] transition-all duration-200 hover:-translate-y-0.5 shadow-sm whitespace-nowrap"
            >
              Enquire Now
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-full text-[#1E3A8A] hover:bg-blue-50 transition-colors"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Dropdown */}
      {isOpen && (
        <div className="lg:hidden bg-white border-t border-blue-50 shadow-lg slide-down" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                className={`flex items-center px-4 py-3 rounded-2xl text-sm font-medium transition-all ${
                  location.pathname === link.path
                    ? 'bg-blue-50 text-[#1E3A8A] font-bold'
                    : 'text-slate-700 hover:bg-blue-50 hover:text-[#1E3A8A]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-2">
              <Link
                to="/admissions"
                className="flex items-center justify-center w-full bg-[#1E3A8A] text-white rounded-full py-3 text-sm font-bold hover:bg-[#1E40AF] transition-colors"
                data-testid="mobile-enquire-btn"
              >
                Enquire Now
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
